import type { StoreApi } from "zustand";
import { create } from "zustand";
import type { Node, Product, ProductAccessory } from "interfaces/schemas";
import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import { isAnyProduct } from "utils/treeUtils";

interface ClipBoardState {
  copiedItem: NodeApi<Node> | null;
  setCopiedItem: (copiedItem: NodeApi<Node> | null) => void;
  setQuantity: (newQuantity: number) => void;
  isHoveringLeftControls: boolean;
  setIsHoveringLeftControls: (hover: boolean) => void;
  reset: () => void;
}

export const useClipboardStore = create<ClipBoardState>()(
  (set, _get, api: StoreApi<ClipBoardState>) => ({
    copiedItem: null,
    setCopiedItem: (copiedItem) => set({ copiedItem }),
    setQuantity: (newQuantity) => {
      const copiedItem = _get().copiedItem;
      if (!copiedItem) {
        console.error("No item is currently copied. Cannot set quantity.");
        return;
      }
      if (!isAnyProduct(copiedItem.data)) {
        console.error("The copied item is not a product. Cannot set quantity.");
        return;
      }
      const newData = { ...copiedItem.data } as Product;
      newData.quantity = newQuantity;
      newData.nodes?.forEach((accessory) => {
        const item = accessory as ProductAccessory;
        item.quantity = newQuantity;
      });
      set({
        copiedItem: {
          ...copiedItem,
          data: newData,
        } as NodeApi<Node>,
      });
    },
    isHoveringLeftControls: false,
    setIsHoveringLeftControls: (hover) =>
      set({ isHoveringLeftControls: hover }),
    reset: () => set(api.getInitialState()),
  }),
);

export const useCopiedItem = () =>
  useClipboardStore((state) => state.copiedItem);
export const useSetCopiedItem = () =>
  useClipboardStore((state) => state.setCopiedItem);
export const useIsHoveringLeftControls = () =>
  useClipboardStore((state) => state.isHoveringLeftControls);
export const useSetIsHoveringLeftControls = () =>
  useClipboardStore((state) => state.setIsHoveringLeftControls);
export const useSetQuantity = () =>
  useClipboardStore((state) => state.setQuantity);
export const useResetClipboard = () =>
  useClipboardStore((state) => state.reset);
